// /Redux/Slices/app-state-slice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppEnumsRecord } from '@/Shared';

/**
 * Translation
 *
 * Represents the currently loaded translation payload used by the application.
 * This structure is designed to support runtime language switching,
 * offline/online awareness, and cache invalidation based on timestamps.
 */
export type Translation = {
    /** Timestamp (in milliseconds) indicating when the translation was generated or fetched */
    date: number;

    /** Language code associated with the translation (e.g. 'en', 'fr') */
    lang: string;

    /** Availability status of the translation source */
    status: 'online' | 'offline';

    /** Key-value map containing translated strings */
    content: Record<string, string>;
};

/**
 * AppState
 *
 * Global application state slice.
 * This slice stores cross-cutting, app-wide data that does not belong
 * to a specific domain entity, including:
 * - application readiness flag
 * - enumerations used throughout the UI
 * - currently active translation data
 */
export interface AppState {
    /** Indicates whether the application has completed its initialization phase */
    appIsReady: boolean;

    /** Centralized enum definitions shared across the application */
    enums: AppEnumsRecord | null;

    /** Currently loaded translation data */
    translation: Translation | null;
}

/**
 * Initial state for the application slice.
 *
 * The app starts in a non-ready state, with no enums or translations loaded.
 */
const initialState: AppState = {
    appIsReady: false,
    enums: null,
    translation: null,
};

/**
 * appStateSlice
 *
 * Redux slice responsible for managing global application state.
 * This slice focuses on initialization and configuration data
 * required by the app before rendering core features.
 *
 * Reducers are intentionally minimal and synchronous,
 * acting as setters for data resolved by external processes
 * (e.g. bootstrap logic, API calls).
 */
export const appStateSlice = createSlice({
    name: 'appState',
    initialState,
    reducers: {

        /**
         * Set the active translation payload.
         *
         * Typically dispatched after fetching translation data
         * from a remote or local source.
         *
         * @param state - Current application state
         * @param action - Translation payload to apply
         */
        setTranslation(state, action: PayloadAction<Translation>) {
            state.translation = action.payload;
        },

        /**
         * Set the global enumeration registry.
         *
         * Enums are commonly used to normalize values
         * received from the backend and consumed by the UI.
         *
         * @param state - Current application state
         * @param action - Enum record to register
         */
        setEnums(state, action: PayloadAction<AppEnumsRecord>) {
            state.enums = action.payload;
        },

        /**
         * Update the application readiness flag.
         *
         * This flag is generally toggled once all critical
         * bootstrap operations have completed.
         *
         * @param state - Current application state
         * @param action - Readiness status
         */
        setAppIsReady(state, action: PayloadAction<boolean>) {
            state.appIsReady = action.payload;
        },
    },
});

export default appStateSlice.reducer;

/**
 * appStateActions / appStateReducer
 *
 * Named exports for convenient integration into the Redux store
 * and for dispatching actions throughout the application.
 */
export const { actions: appStateActions, reducer: appStateReducer } = appStateSlice;
